import React from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { queryClient } from 'src/lib/react-query';
import ConfirmationModal from 'src/registry/components/blocks/ConfirmationModal';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { toast } from 'src/hooks/useToast';
import { ICON_MAP } from 'src/utils/constants';
import { useOnError } from 'src/utils/errors';
import { useDeleteSubscriptionItemPricingOverride } from 'src/features/subscriptionItemPricingOverrides/api/useDeleteSubscriptionItemPricingOverride';
import { LIST_SUBSCRIPTION_ITEM_PRICING_OVERRIDES_RESOURCE } from 'src/features/subscriptionItemPricingOverrides/api/useListSubscriptionItemPricingOverrides';
import { LIST_SUBSCRIPTION_ITEMS_RESOURCE } from 'src/features/subscriptionItems/api/useListSubscriptionItems';
import { pricingOverridesMessages } from 'src/features/subscriptionItemPricingOverrides/utils/messages';
import { SubscriptionItemPricingOverride } from 'src/features/subscriptionItemPricingOverrides/utils/types';

interface DeleteSubscriptionItemPricingOverrideFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedPricingOverride: SubscriptionItemPricingOverride;
    children?: React.ReactNode;
}

function DeleteSubscriptionItemPricingOverrideFormModal<TData>({
    selectedPricingOverride,
    children,
}: DeleteSubscriptionItemPricingOverrideFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage } = useIntl();
    const { subscriptionId = '', subscriptionItemId = '' } = useParams();

    const onError = useOnError();

    const { mutate: deletePricingOverride } = useDeleteSubscriptionItemPricingOverride({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_SUBSCRIPTION_ITEM_PRICING_OVERRIDES_RESOURCE] });
            void queryClient.invalidateQueries({ queryKey: [LIST_SUBSCRIPTION_ITEMS_RESOURCE] });
            setIsModalOpen(false);
            toast({
                title: formatMessage(pricingOverridesMessages.deletePricingOverrideSuccess),
                description: formatMessage(pricingOverridesMessages.deletePricingOverrideSuccessDescription),
            });
        },
        onError: (error) => {
            onError(error, () => null);
        },
    });

    return (
        <ConfirmationModal
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            dialogDetails={{
                title: { ...pricingOverridesMessages.deletePricingOverride },
                subTitle: { ...pricingOverridesMessages.deletePricingOverrideSubtitle },
                icon: ICON_MAP.DELETE,
                confirmationMessage: { ...pricingOverridesMessages.confirmDeletePricingOverride },
            }}
            callbackButton={{
                variant: 'destructiveLight',
                buttonTitle: pricingOverridesMessages.deletePricingOverride,
                callbackFn: () => {
                    deletePricingOverride({
                        subscriptionId,
                        itemId: subscriptionItemId,
                        overrideId: selectedPricingOverride.id,
                    });
                    setIsModalOpen(false);
                },
            }}
            children={children}
        />
    );
}

export default DeleteSubscriptionItemPricingOverrideFormModal;
